import { ChangeDetectorRef, Directive, inject, Input, OnDestroy, OnInit } from '@angular/core'
import { Subscription } from 'rxjs'

import { SnapshotContext, SnapshotResponse } from '@data/interfaces/verification-snapshot.interface'
import { RiskScorecardView, ScorecardResult } from '@data/interfaces/risk-scorecard.interface'
import { loadErrorUiState, SnapshotUiState } from './snapshot-state/snapshot-state.component'
import { RiskScorecardStore } from './risk-scorecard.store'

/**
 * Lo común de Flujos, Estados financieros, Ratios y Calculadora: mirar la respuesta compartida de la calculadora
 * (`RiskScorecardStore`, una sola petición por vista) y traducir el sobre a un estado de `app-snapshot-state`.
 * Como las de Buró y Legal, se instancian al primer clic (`pTemplate="content"`) y es entonces cuando piden.
 */
@Directive()
export abstract class RiskScorecardTabBase implements OnInit, OnDestroy {
  @Input() context: SnapshotContext | null = null

  view: RiskScorecardView | null = null
  result: ScorecardResult | null = null
  /** Estado que sustituye a la pestaña entera; `null` cuando hay resultado que pintar. */
  envelopeState: SnapshotUiState | null = 'loading'

  protected readonly store = inject(RiskScorecardStore)
  protected readonly cd = inject(ChangeDetectorRef)
  private subscription: Subscription | null = null

  ngOnInit(): void {
    if (!this.context) return
    this.subscription = this.store.watch(this.context).subscribe((response) => {
      this.apply(response)
      this.cd.markForCheck()
    })
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe()
  }

  retry(): void {
    if (this.context) this.store.reload(this.context)
  }

  trackByIndex(index: number): number {
    return index
  }

  /** Recalcula lo derivado cada vez que llega una respuesta con resultado. */
  protected onResult(): void {
    // Cada pestaña deriva aquí lo que pinta.
  }

  private apply(response: SnapshotResponse<RiskScorecardView> | null): void {
    this.view = response && !response.error ? response.data : null
    this.result = this.view?.result ?? null
    this.envelopeState = envelopeOf(response)
    if (!this.envelopeState) this.onResult()
  }
}

/** El estado de la pestaña entera, o `null` si hay resultado que pintar. */
export function envelopeOf(response: SnapshotResponse<RiskScorecardView> | null): SnapshotUiState | null {
  if (!response) return 'loading'
  if (response.error || !response.data) return loadErrorUiState(response)
  const state = response.data.snapshot?.state
  if (state === 'pre_snapshot') return 'pre-snapshot'
  if (state === 'no_verification' || !response.data.snapshot) return 'no-verification'
  return response.data.result ? null : 'unavailable'
}
